<script setup>
import { router, useForm, usePage, usePoll } from '@inertiajs/vue3'
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { Overlay } from 'ol'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'
import ModelSelector from '@/components/form/ModelSelector.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import PromotionApprovalDialog from '@/components/admin/PromotionApprovalDialog.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import { MAP_DEFAULT_BASELAYER, MAP_PROJECTION } from '@/constants'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { transform } from '@/services/map/ProjectionService'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  runs: { type: Array, default: () => [] },
  formDefaults: { type: Object, default: () => ({}) },
  maakonnad: { type: Array, default: () => [] },
  models: { type: Array, default: () => [] },
  defaultModel: { type: String, default: null },
})

const page = usePage()
const flash = computed(() => page.props.flash ?? {})

// ─── Launch form ─────────────────────────────────────────────────────────────
const form = useForm({
  limit: props.formDefaults.limit ?? 500,
  months: props.formDefaults.months ?? 12,
  maakond: props.formDefaults.maakond ?? 'Harjumaa',
  name: '',
  // null = aktiivne vaikimisi mudel; kandidaadid (autoresearch) on valitavad
  // ja skaneeritavad enne promotet.
  model_type: props.defaultModel ?? null,
})

const maakondOptions = computed(() =>
  props.maakonnad.map(m => ({ label: m === 'all' ? 'Kogu Eesti' : m, value: m })),
)

const launch = () => {
  form.post(route('admin.accuracy-scan.run'), { preserveScroll: true })
}

// ─── Live progress (SimilarityCalibration.vue pattern) ──────────────────────
const POLL_INTERVAL = 2500
const anyActive = computed(() => props.runs.some(r => r.status === 'pending' || r.status === 'running'))
const { start, stop } = usePoll(POLL_INTERVAL, { only: ['runs'] }, { autoStart: false })
watch(anyActive, active => (active ? start() : stop()), { immediate: true })
onBeforeUnmount(stop)

const pct = v => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`)
const eur = v =>
  v === null || v === undefined ? '—' : `${Math.round(Number(v)).toLocaleString('et-EE')} €`
const date = iso => (iso ? new Date(iso).toLocaleString('et-EE') : '—')

const statusSeverity = s =>
  ({ completed: 'success', failed: 'danger', running: 'info', pending: 'warn' })[s] ?? 'secondary'
const isActive = r => r.status === 'pending' || r.status === 'running'
const progressPct = r => (r.progress_total > 0 ? Math.round((r.progress_current / r.progress_total) * 100) : 0)
const stageLabel = r =>
  ({
    queued: 'Ootel järjekorras',
    assembling: 'Koostan andmestikku',
    analyzing: 'Analüüsin tehinguid',
    summarizing: 'Koostan kokkuvõtet',
  })[r.progress_stage] ?? r.status

// ─── Run detail (map + segments) ─────────────────────────────────────────────
const detail = shallowRef(null)
const detailLoading = ref(false)
const detailError = ref(null)
const selectedRunId = ref(null)

const loadDetail = async run => {
  selectedRunId.value = run.id
  detailLoading.value = true
  detailError.value = null
  closePopup()
  try {
    const response = await fetch(route('admin.accuracy-scan.show', run.id), {
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    detail.value = await response.json()
    clusterResult.value = detail.value.cluster_summary ?? null
    mapMode.value = 'error'
    clusterError.value = null
    await nextTick()
    renderMap()
  } catch (e) {
    detailError.value = e.message
  } finally {
    detailLoading.value = false
  }
}

// ─── Segmentation (supervised tree) + diagnostic clustering (k-means) ───────
const clusterResult = shallowRef(null)
const clusterLoading = ref(false)
const clusterError = ref(null)
const clusterMethod = ref('tree')
const clusterMethodOptions = [
  { label: 'Segmendipuu (täpne)', value: 'tree' },
  { label: 'A-priori (tihedus + eritüüp)', value: 'apriori' },
  { label: 'K-means (diagnostika)', value: 'kmeans' },
]
const clusterK = ref(null) // kmeans: null/empty = auto (best silhouette)
const clusterResidualWeight = ref(0.5) // kmeans: km of separation per pp of signed error
const treeMaxDepth = ref(3)
const treeMinLeaf = ref(20)
const aprioriAuto = ref(true) // discover optimal thresholds from the data
const aprioriAgeThreshold = ref(null) // null = no "vana hoone" segment
const aprioriAreaThreshold = ref(null) // null = no "suur pind" segment
const aprioriDensityThresholds = ref('10,100') // comma-separated, ascending, 1-3 values

const clusterRequestBody = () => {
  if (clusterMethod.value === 'tree') {
    return { method: 'tree', max_depth: treeMaxDepth.value, min_leaf: treeMinLeaf.value }
  }
  if (clusterMethod.value === 'apriori') {
    if (aprioriAuto.value) {
      return { method: 'apriori', auto: true }
    }
    return {
      method: 'apriori',
      age_threshold: aprioriAgeThreshold.value || null,
      area_threshold: aprioriAreaThreshold.value || null,
      density_thresholds: aprioriDensityThresholds.value
        .split(',')
        .map(v => parseInt(v.trim(), 10))
        .filter(v => Number.isInteger(v) && v >= 0),
    }
  }
  return { method: 'kmeans', k: clusterK.value || null, residual_km_per_pct: clusterResidualWeight.value, seed: 1 }
}

const CLUSTER_PALETTE = [
  '#e11d48', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2',
  '#be185d', '#4d7c0f', '#b45309', '#1e40af', '#0f766e', '#9333ea',
]
const clusterColor = id => CLUSTER_PALETTE[(id - 1) % CLUSTER_PALETTE.length]

const runCluster = async () => {
  if (!detail.value) {
    return
  }
  clusterLoading.value = true
  clusterError.value = null
  try {
    const response = await fetch(route('admin.accuracy-scan.cluster', detail.value.run.id), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify(clusterRequestBody()),
    })
    const data = await response.json()
    if (!response.ok || data.error) {
      throw new Error(data.error || `HTTP ${response.status}`)
    }
    clusterResult.value = data
    mapMode.value = 'cluster'
    renderOverlayOnly()
  } catch (e) {
    clusterError.value = e.message
  } finally {
    clusterLoading.value = false
  }
}

const zoomToCluster = cluster => {
  const [x, y] = toMapCoord([cluster.centroid.lon, cluster.centroid.lat])
  const r = Math.max((cluster.radius_km || 1) * 1000 * 1.3, 800)
  mapRef.value?.updateBbox([x - r, y - r, x + r, y + r])
}

const expandedClusters = ref({})

// ─── Per-segment config tuning (segment_tuning) ─────────────────────────────
const segmentTuning = computed(() => detail.value?.segment_tuning ?? null)
const tuneDepth = ref(3)
const tuneMinLeaf = ref(60)
const tuneSegmentation = ref('cart')
const tuneSegmentationOptions = [
  { label: 'CART puu', value: 'cart' },
  { label: 'A-priori', value: 'apriori' },
]
// Follow the persisted segmentation: a run frozen as apriori should tune apriori by default.
watch(
  () => detail.value?.cluster_summary?.method,
  method => {
    tuneSegmentation.value = method === 'apriori' ? 'apriori' : 'cart'
  },
)
const tuneLaunching = ref(false)

const launchTuning = () => {
  if (!detail.value) {
    return
  }
  tuneLaunching.value = true
  router.post(
    route('admin.accuracy-scan.tune-segments', detail.value.run.id),
    { depth: tuneDepth.value, min_tune: tuneMinLeaf.value, segmentation: tuneSegmentation.value },
    {
      preserveScroll: true,
      onFinish: () => {
        tuneLaunching.value = false
        // Re-read the detail so the "running" state shows immediately.
        loadDetail({ id: detail.value.run.id })
      },
    },
  )
}

// RED-355 B3: "Rakenda segmendid" is an approved promotion (approver + Jira link + reason).
const promoteDialogVisible = ref(false)
const canPromoteSegments = computed(() => {
  const tuning = segmentTuning.value
  if (tuning?.status !== 'completed' || tuning?.params?.segmentation !== 'apriori') {
    return false
  }
  return Object.values(tuning.nodes ?? {}).some(n => n.is_leaf && n.beats_baseline)
})
const promoteSegments = () => {
  promoteDialogVisible.value = true
}
const promoteSegmentsUrl = computed(() =>
  detail.value?.run ? route('admin.accuracy-scan.promote-segments', detail.value.run.id) : '',
)

// ALL tuned nodes, root first — showing only the best cut's nodes hides exactly what the
// view exists for: when the global cut wins the leaderboard, the per-segment verdicts and
// configs vanished (observed on run 15, where the table collapsed to just "root").
const tunedNodes = computed(() => {
  const tuning = segmentTuning.value
  if (!tuning || tuning.status !== 'completed') {
    return []
  }
  // Object.values tolerates both shapes: the list contract and older object-keyed payloads.
  return Object.values(tuning.nodes ?? {}).sort((a, b) => {
    if (a.key === 'root') {
      return -1
    }
    if (b.key === 'root') {
      return 1
    }
    return (a.leaf_id ?? 0) - (b.leaf_id ?? 0) || a.key.localeCompare(b.key)
  })
})

const bestCutKeys = computed(() => new Set(segmentTuning.value?.cuts?.[0]?.segments ?? []))

const nodeLabel = node => {
  if (!node) {
    return '—'
  }
  if (node.is_leaf && segmentTuning.value?.leaf_rules?.[node.leaf_id]) {
    return segmentTuning.value.leaf_rules[node.leaf_id]
  }
  return node.key === 'root' ? 'kogu valim' : `lehtede #${(node.leaf_ids ?? []).join(' + #')} ühend`
}

const configText = config => {
  if (!config) {
    return '—'
  }
  const embed = Array.isArray(config.embed_weights)
    ? (config.embed_weights.reduce((a, b) => a + Number(b), 0) / config.embed_weights.length).toFixed(2)
    : '—'
  return `zdist ${Number(config.zdist_weight).toFixed(2)} · price ${Number(config.price_diff_weight).toFixed(2)} · rikastatus ${Number(config.rikastatus_weight).toFixed(2)} · embed ⌀${embed}`
}

const improvementPct = cut =>
  cut.weighted_baseline_mape !== null && cut.weighted_baseline_mape > 0
    ? Math.round((1 - cut.weighted_tuned_mape / cut.weighted_baseline_mape) * 100)
    : null

const CLUSTER_ATTR_LABELS = [
  { key: 'rooms', label: 'Tube', unit: '' },
  { key: 'area', label: 'Pind', unit: ' m²' },
  { key: 'building_age', label: 'Hoone vanus', unit: ' a' },
  { key: 'tehingusumma', label: 'Hind', unit: ' €' },
]

const attrNumber = (v, key) => {
  if (v === null || v === undefined) {
    return '—'
  }
  return key === 'tehingusumma' ? `${Math.round(v / 1000)} k` : Math.round(v)
}

const directionLabel = d =>
  ({ over: 'Ülehinnatud', under: 'Alahinnatud', balanced: 'Tasakaalus' })[d] ?? d
const directionSeverity = d => ({ over: 'danger', under: 'info', balanced: 'success' })[d] ?? 'secondary'

const fidelityLabel = f =>
  ({ exact: 'Täpne reegel', good: 'Eristatav segment', partial: 'Osaliselt eristatav', poor: 'Diagnostiline' })[f] ?? f
const fidelitySeverity = f =>
  ({ exact: 'success', good: 'success', partial: 'warn', poor: 'secondary' })[f] ?? 'secondary'

// ─── Map: signed-error point cloud ───────────────────────────────────────────
const { findLayer } = useDetailMapHelpers()
const mapRef = ref(null)
const olMap = shallowRef(null)
const popupEl = ref(null)
const popupPoint = shallowRef(null)
let popupOverlay = null

const mapview = {
  baseLayers: [findLayer(MAP_DEFAULT_BASELAYER, false)],
  baseLayer: MAP_DEFAULT_BASELAYER,
  center: transform('fromLonLat', [24.75, 59.43], MAP_PROJECTION),
}
const toMapCoord = coords => transform('fromLonLat', coords, MAP_PROJECTION)

// Diverging palette over signed error, clamped to ±30%: blue = model UNDER actual price,
// neutral ≈ accurate, red = model OVER. The sequential heatmap gradient is wrong for signed
// data — it would paint −30% and +30% as "same intensity, different amounts of bad".
const ERROR_CLAMP = 30
const BLUE = [29, 78, 216]
const NEUTRAL = [241, 245, 249]
const RED = [220, 38, 38]
const lerp = (a, b, t) => Math.round(a + (b - a) * t)
const errorColor = (errorPct, alpha = 0.9) => {
  const t = Math.max(-1, Math.min(1, (errorPct ?? 0) / ERROR_CLAMP))
  const [from, to] = t < 0 ? [NEUTRAL, BLUE] : [NEUTRAL, RED]
  const k = Math.abs(t)
  return `rgba(${lerp(from[0], to[0], k)}, ${lerp(from[1], to[1], k)}, ${lerp(from[2], to[2], k)}, ${alpha})`
}
const legendGradient = `linear-gradient(to right, rgb(${BLUE.join(',')}), rgb(${NEUTRAL.join(',')}), rgb(${RED.join(',')}))`

const pointsById = computed(() => {
  const byId = new Map()
  for (const p of detail.value?.points || []) {
    byId.set(p.transaction_id, p)
  }
  return byId
})

// 'error' colors points by signed error; 'cluster' colors by suggested-segment membership.
const mapMode = ref('error')
const mapModeOptions = [
  { label: 'Viga', value: 'error' },
  { label: 'Klastrid', value: 'cluster' },
]
watch(mapMode, () => renderOverlayOnly())

// Approximate a geographic circle as a 64-gon in map units (EPSG:3301 is metric).
const circlePolygon = (center, radiusM) => {
  const ring = []
  for (let i = 0; i <= 64; i++) {
    const a = (i / 64) * 2 * Math.PI
    ring.push([center[0] + radiusM * Math.cos(a), center[1] + radiusM * Math.sin(a)])
  }
  return ring
}

const buildFeatures = () => {
  const features = []
  const clusterMode = mapMode.value === 'cluster' && clusterResult.value
  const assignments = clusterResult.value?.assignments ?? {}

  for (const p of detail.value?.points || []) {
    if (p.lon === null || p.lat === null) {
      continue
    }
    const clusterId = clusterMode ? (assignments[p.transaction_id] ?? null) : null
    features.push({
      type: 'Feature',
      properties: {
        kind: 'scan-point',
        id: p.transaction_id,
        failed: p.status !== 'ok',
        errorPct: p.error_pct,
        clusterFill: clusterMode ? (clusterId !== null ? clusterColor(clusterId) : 'rgba(148, 163, 184, 0.5)') : null,
      },
      geometry: { type: 'Point', coordinates: toMapCoord([p.lon, p.lat]) },
    })
  }

  if (clusterMode) {
    const isTree = clusterResult.value.method === 'tree'
    for (const c of clusterResult.value.clusters) {
      if (!c.centroid) {
        continue
      }
      const center = toMapCoord([c.centroid.lon, c.centroid.lat])
      // Radius circles only for k-means — a tree segment is defined by its rule, not a disc,
      // and may be geographically dispersed (e.g. split on price or area).
      if (!isTree && c.radius_km !== null) {
        features.push({
          type: 'Feature',
          properties: { kind: 'cluster-radius', color: clusterColor(c.id) },
          geometry: { type: 'Polygon', coordinates: [circlePolygon(center, Math.max(c.radius_km * 1000, 300))] },
        })
      }
      features.push({
        type: 'Feature',
        properties: { kind: 'cluster-centroid', label: String(c.id), color: clusterColor(c.id) },
        geometry: { type: 'Point', coordinates: center },
      })
    }
  }

  return features
}

const featureStyle = feature => {
  const kind = feature.get('kind')

  if (kind === 'cluster-radius') {
    return new Style({
      stroke: new Stroke({ color: feature.get('color'), width: 1.5, lineDash: [6, 4] }),
      fill: new Fill({ color: 'rgba(100, 116, 139, 0.05)' }),
      zIndex: 1,
    })
  }

  if (kind === 'cluster-centroid') {
    return new Style({
      image: new CircleStyle({
        radius: 11,
        fill: new Fill({ color: feature.get('color') }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new Text({
        text: feature.get('label'),
        font: 'bold 11px sans-serif',
        fill: new Fill({ color: '#ffffff' }),
      }),
      zIndex: 100,
    })
  }

  if (feature.get('failed')) {
    return new Style({
      image: new CircleStyle({
        radius: 4,
        fill: new Fill({ color: 'rgba(148, 163, 184, 0.4)' }),
        stroke: new Stroke({ color: '#64748b', width: 1 }),
      }),
      zIndex: 5,
    })
  }

  const clusterFill = feature.get('clusterFill')
  if (clusterFill) {
    return new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: clusterFill }),
        stroke: new Stroke({ color: '#ffffff', width: 1 }),
      }),
      zIndex: 10,
    })
  }

  const err = feature.get('errorPct') ?? 0
  const emphasis = Math.min(Math.abs(err) / ERROR_CLAMP, 1)
  return new Style({
    image: new CircleStyle({
      radius: 5 + emphasis * 3,
      fill: new Fill({ color: errorColor(err) }),
      stroke: new Stroke({ color: '#ffffff', width: 1 }),
    }),
    zIndex: 10 + Math.round(emphasis * 20),
  })
}

const featuresExtent = features => {
  if (!features.length) {
    return null
  }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const f of features) {
    const [x, y] = f.geometry.coordinates
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }
  const padX = (maxX - minX) * 0.05 || 400
  const padY = (maxY - minY) * 0.05 || 400
  return [minX - padX, minY - padY, maxX + padX, maxY + padY]
}

const buildOverlay = () => ({
  id: 'accuracy-scan-points',
  type: 'FeatureCollection',
  features: buildFeatures(),
  style: featureStyle,
  clusters: false,
})

const renderOverlayOnly = () => {
  mapRef.value?.updateOverlay(buildOverlay())
}

const renderMap = () => {
  if (!mapRef.value) {
    return
  }
  const overlay = buildOverlay()
  mapRef.value.updateOverlay(overlay)
  const extent = featuresExtent(overlay.features)
  if (extent) {
    mapRef.value.updateBbox(extent)
  }
}

const handleMapClick = evt => {
  let feature = null
  olMap.value?.forEachFeatureAtPixel(
    evt.pixel,
    f => {
      if (f.get('kind') === 'scan-point') {
        feature = f
        return true
      }
      return false
    },
    { hitTolerance: 4 },
  )
  if (!feature) {
    closePopup()
    return
  }
  popupPoint.value = pointsById.value.get(feature.get('id')) ?? null
  nextTick(() => popupOverlay?.setPosition(feature.getGeometry().getCoordinates()))
}

const closePopup = () => {
  popupPoint.value = null
  popupOverlay?.setPosition(undefined)
}

const onMapReady = map => {
  olMap.value = map
  if (popupEl.value) {
    popupOverlay = new Overlay({
      element: popupEl.value,
      positioning: 'bottom-center',
      offset: [0, -12],
      stopEvent: true,
    })
    map.addOverlay(popupOverlay)
  }
  map.on('singleclick', handleMapClick)
  map.updateSize()
  if (detail.value) {
    renderMap()
  }
}

onBeforeUnmount(() => {
  olMap.value?.un('singleclick', handleMapClick)
})

// ─── Segment tables ──────────────────────────────────────────────────────────
const regionLevel = ref('omavalitsus')
const regionLevelOptions = [
  { label: 'Maakond', value: 'maakond' },
  { label: 'Omavalitsus', value: 'omavalitsus' },
  { label: 'Asum / linnaosa', value: 'asum' },
]

const regionRows = computed(() => {
  const rows = detail.value?.segment_summary?.regions?.[regionLevel.value] ?? []
  return rows.map(r => ({
    ...r,
    label: r.asum ?? r.omavalitsus ?? r.maakond ?? '—',
  }))
})

const attributeRows = computed(() => {
  const attrs = detail.value?.segment_summary?.attributes ?? {}
  const groupLabels = {
    building_age: 'Hoone vanus',
    area: 'Pindala',
    price_class: 'Hinnaklass',
    rooms: 'Tube',
    floor: 'Korrus',
    enrichment: 'Rikastatus',
  }
  return Object.entries(attrs).flatMap(([group, entries]) =>
    entries.map(e => ({ ...e, group: groupLabels[group] ?? group })),
  )
})

const biasClass = bias => {
  if (bias === null || bias === undefined) {
    return ''
  }
  return bias > 0 ? 'text-red-600' : 'text-blue-700'
}
</script>

<template>
  <div class="py-4">
    <div class="mb-3">
      <h1 class="text-xl font-medium">Segmendianalüüs</h1>
      <p class="text-surface-500 text-sm">
        Kus praegune sarnasuskonfiguratsioon eksib? Skaneering hindab värsked holdout-tehingud
        läbi (kaalutud keskmine vs tegelik tehingusumma) ja jaotab vea piirkondade ning
        segmentide kaupa — sisend tulevastele piirkondlikele kalibratsioonidele.
      </p>
    </div>

    <Message v-if="flash.success" severity="success" :closable="false" class="mb-3">
      {{ flash.success }}
    </Message>
    <Message v-if="flash.error" severity="error" :closable="false" class="mb-3">
      {{ flash.error }}
    </Message>

    <!-- Launch -->
    <Card class="mb-4">
      <template #title>
        <span class="text-xl font-medium">Käivita skaneering</span>
      </template>
      <template #content>
        <p class="text-surface-500 text-xs mb-3">
          Iga tehing analüüsitakse aktiivse kalibreeritud konfiguratsiooniga (üks /v2/analyze
          päring tehingu kohta) — 500 tehingut võtab hinnanguliselt 10–45 minutit. Skaneering
          jagab kalibreerimisega sama järjekorda, seega suur skaneering ja kalibreerimine
          ootavad teineteist.
        </p>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs text-surface-500">Tehinguid</label>
            <InputNumber v-model="form.limit" :min="1" :max="2000" fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-surface-500">Ajaperiood (kuud)</label>
            <InputNumber v-model="form.months" :min="1" :max="60" fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-surface-500">Piirkond</label>
            <Select v-model="form.maakond" :options="maakondOptions" optionLabel="label" optionValue="value" fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-surface-500">Nimi (valikuline)</label>
            <InputText v-model="form.name" fluid />
          </div>
          <div class="col-span-2 flex flex-col gap-1">
            <label class="text-xs text-surface-500">Mudel</label>
            <ModelSelector v-model="form.model_type" :models="models" type="ebm" placeholder="Aktiivne vaikimisi mudel" class="min-w-0!" />
          </div>
        </div>
        <div class="flex justify-end">
          <Button label="Käivita skaneering" icon="pi pi-play" :loading="form.processing" @click="launch" />
        </div>
      </template>
    </Card>

    <!-- Runs -->
    <Card class="mb-4">
      <template #title>
        <span class="text-xl font-medium">Skaneeringud</span>
      </template>
      <template #content>
        <DataTable :value="runs" dataKey="id" size="small" stripedRows :rowHover="true">
          <Column header="Aeg">
            <template #body="{ data }">
              <div class="text-sm">{{ date(data.created_at) }}</div>
              <div v-if="data.name" class="text-xs text-surface-400">{{ data.name }}</div>
            </template>
          </Column>
          <Column header="Olek" style="min-width: 16rem">
            <template #body="{ data }">
              <div v-if="isActive(data)" class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <i class="pi pi-spin pi-spinner text-primary text-sm" />
                  <span class="text-sm font-medium">{{ stageLabel(data) }}</span>
                  <span v-if="data.progress_total > 0" class="text-surface-400 text-xs">
                    {{ data.progress_current }}/{{ data.progress_total }}
                  </span>
                </div>
                <ProgressBar v-if="data.progress_total > 0" :value="progressPct(data)" style="height: 0.4rem" />
                <ProgressBar v-else mode="indeterminate" style="height: 0.4rem" />
              </div>
              <template v-else>
                <Tag :value="data.status" :severity="statusSeverity(data.status)" />
              </template>
            </template>
          </Column>
          <Column header="Piirkond">
            <template #body="{ data }">
              {{ data.params?.maakond === 'all' ? 'Kogu Eesti' : (data.params?.maakond ?? '—') }}
            </template>
          </Column>
          <Column>
            <template #header>
              <span class="inline-flex items-center gap-1">
                MAPE
                <i
                  class="pi pi-info-circle text-surface-400 text-xs cursor-help"
                  v-tooltip.top="'Keskmine absoluutne protsentuaalne viga: kaalutud keskmine hind vs tegelik tehingusumma üle kõigi õnnestunud tehingute. Väiksem on parem.'"
                />
              </span>
            </template>
            <template #body="{ data }">{{ pct(data.mape) }}</template>
          </Column>
          <Column>
            <template #header>
              <span class="inline-flex items-center gap-1">
                Bias
                <i
                  class="pi pi-info-circle text-surface-400 text-xs cursor-help"
                  v-tooltip.top="'Keskmine MÄRGIGA viga: + tähendab, et mudel hindab keskmiselt üle tegeliku hinna, − alla. 0 lähedal = tasakaalus.'"
                />
              </span>
            </template>
            <template #body="{ data }">
              <span :class="biasClass(data.bias_pct)">{{ pct(data.bias_pct) }}</span>
            </template>
          </Column>
          <Column header="Tehinguid (ok / viga)">
            <template #body="{ data }">{{ data.n_ok }} / {{ data.n_failed }}</template>
          </Column>
          <Column header="Tegevused">
            <template #body="{ data }">
              <Button
                v-if="data.status === 'completed'"
                label="Vaata"
                size="small"
                icon="pi pi-map"
                :outlined="selectedRunId !== data.id"
                :loading="detailLoading && selectedRunId === data.id"
                @click="loadDetail(data)"
              />
              <span v-else-if="data.error" class="text-red-600 text-xs" :title="data.error">
                {{ data.error.slice(0, 60) }}…
              </span>
              <span v-else class="text-surface-400 text-sm">—</span>
            </template>
          </Column>
          <template #empty>
            <div class="text-center text-surface-400 py-6">Skaneeringuid pole veel tehtud.</div>
          </template>
        </DataTable>
      </template>
    </Card>

    <Message v-if="detailError" severity="error" :closable="false" class="mb-3">
      Detailide laadimine ebaõnnestus: {{ detailError }}
    </Message>

    <!-- Detail: error map + segment tables -->
    <template v-if="detail">
      <Card class="mb-4">
        <template #title>
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-3">
              <span class="text-xl font-medium">Veakaart</span>
              <span class="text-sm text-surface-500">
                MAPE {{ pct(detail.run.mape) }} · bias
                <span :class="biasClass(detail.run.bias_pct)">{{ pct(detail.run.bias_pct) }}</span>
                · {{ detail.run.n_ok }} tehingut · mudel {{ detail.run.config?.model_type ?? '—' }}
              </span>
            </div>
            <SelectButton
              v-if="clusterResult"
              v-model="mapMode"
              :options="mapModeOptions"
              optionLabel="label"
              optionValue="value"
              size="small"
            />
          </div>
        </template>
        <template #content>
          <div class="relative h-[520px] rounded-sm overflow-hidden">
            <MapComponent
              ref="mapRef"
              class="w-full h-full"
              :center="mapview.center"
              :zoom="8"
              :min-zoom="0"
              :max-zoom="16"
              projection="EPSG:3301"
              :base-layers="mapview.baseLayers"
              :base-layer="mapview.baseLayer"
              :zoom-control="false"
              @ready="onMapReady"
            />

            <!-- Diverging legend: signed error, not intensity -->
            <div
              v-if="mapMode === 'error'"
              class="absolute bottom-3 left-3 bg-white/90 backdrop-blur-xs rounded-md shadow-sm px-3 py-2 z-10"
            >
              <div class="text-xs text-surface-600 mb-1">Viga tegelikust hinnast</div>
              <div class="h-2 w-48 rounded-sm" :style="{ background: legendGradient }" />
              <div class="flex justify-between text-[10px] text-surface-500 w-48">
                <span>−{{ ERROR_CLAMP }}% (alla)</span>
                <span>0</span>
                <span>+{{ ERROR_CLAMP }}% (üle)</span>
              </div>
            </div>

            <!-- Click popup -->
            <div ref="popupEl" class="z-20">
              <div v-if="popupPoint" class="bg-white rounded-lg shadow-lg border border-surface-200 p-3 w-64 text-sm">
                <div class="flex items-start justify-between gap-2 mb-1">
                  <span class="font-medium">{{ popupPoint.address ?? `Tehing #${popupPoint.transaction_id}` }}</span>
                  <button class="text-surface-400 hover:text-surface-600" @click="closePopup">
                    <i class="pi pi-times text-xs" />
                  </button>
                </div>
                <div class="text-xs text-surface-500 mb-2">
                  {{ [popupPoint.ma_asum_linnaosa, popupPoint.omavalitsus, popupPoint.maakond].filter(Boolean).join(', ') }}
                </div>
                <div class="grid grid-cols-2 gap-y-0.5 text-xs">
                  <span class="text-surface-500">Tegelik hind</span>
                  <span class="font-medium">{{ eur(popupPoint.tehingusumma) }}</span>
                  <span class="text-surface-500">Mudeli hinnang</span>
                  <span class="font-medium">{{ eur(popupPoint.predicted_weighted) }}</span>
                  <span class="text-surface-500">Viga</span>
                  <span class="font-medium" :class="biasClass(popupPoint.error_pct)">{{ pct(popupPoint.error_pct) }}</span>
                  <span class="text-surface-500">Võrdlustehinguid</span>
                  <span class="font-medium">{{ popupPoint.n_comparables }}</span>
                </div>
                <div v-if="popupPoint.status !== 'ok'" class="text-xs text-red-600 mt-1">
                  {{ popupPoint.status }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Residual+location clustering: suggested segments -->
      <Card class="mb-4">
        <template #title>
          <div class="flex flex-wrap items-center gap-3">
            <span class="text-xl font-medium">Segmendid</span>
            <span class="text-sm text-surface-500">
              Segmendipuu: vaadeldavate tunnuste ruum jagatakse vea järgi mittekattuvateks
              segmentideks — iga tehing (ka uus) kuulub täpselt ühte. K-means: diagnostiline vaade.
            </span>
          </div>
        </template>
        <template #content>
          <div class="flex flex-wrap items-end gap-3 mb-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-surface-500">Meetod</label>
              <SelectButton
                v-model="clusterMethod"
                :options="clusterMethodOptions"
                optionLabel="label"
                optionValue="value"
                size="small"
              />
            </div>
            <template v-if="clusterMethod === 'tree'">
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Max sügavus
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Otsustuspuu sügavus: 3 → kuni 8 segmenti, 4 → kuni 16. Sügavam = peenem jaotus, aga väiksemad ja mürarikkamad lehed.'"
                  />
                </label>
                <InputNumber v-model="treeMaxDepth" :min="2" :max="5" class="w-28" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Min tehinguid lehes
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Segment peab sisaldama vähemalt nii palju tehinguid — väiksem leht oleks müra, mitte signaal.'"
                  />
                </label>
                <InputNumber v-model="treeMinLeaf" :min="10" :max="200" class="w-28" />
              </div>
            </template>
            <template v-else-if="clusterMethod === 'apriori'">
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Automaatne
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Leia optimaalsed piirid andmetest: ammendav otsing tiheduse-/vanuse-/pinnapiiride üle (max 5 segmenti, iga segment ≥ 60 rida = häälestatav), skoor = vea hajuvuse vähenemine. Lülita välja, et piirid käsitsi ette anda.'"
                  />
                </label>
                <ToggleSwitch v-model="aprioriAuto" />
              </div>
              <div v-if="!aprioriAuto" class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Tihedusepiirid
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'1–3 kasvavat piiri komadega (nt 10,100): tehinguid 6 km raadiuses / 12 kuud. Iga piir lisab ühe tihedusastme, hõredaim enne. Vajab tx_density veergu (uued skaneeringud; vanadele accuracy-scan:backfill-density).'"
                  />
                </label>
                <InputText v-model="aprioriDensityThresholds" placeholder="10,100" class="w-36" />
              </div>
              <div v-if="!aprioriAuto" class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Vana hoone piir (a)
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Eritüübi segment: hoone vanus > piir. Tühi = eritüüpi ei tehta. Vali segment_summary vanuse-tabelist, kus MAPE selgelt halveneb.'"
                  />
                </label>
                <InputNumber v-model="aprioriAgeThreshold" :min="1" :max="150" placeholder="—" class="w-28" />
              </div>
              <div v-if="!aprioriAuto" class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Suure pinna piir (m²)
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Eritüübi segment: pind > piir. Tühi = eritüüpi ei tehta. Kokku max 5 segmenti (eritüübid + tihedusastmed + tihe piirkond).'"
                  />
                </label>
                <InputNumber v-model="aprioriAreaThreshold" :min="10" :max="500" placeholder="—" class="w-28" />
              </div>
            </template>
            <template v-else>
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500">Klastreid (tühi = automaatne)</label>
                <InputNumber v-model="clusterK" :min="2" :max="12" placeholder="auto" class="w-36" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                  Vea kaal (km / %)
                  <i
                    class="pi pi-info-circle text-surface-400 cursor-help"
                    v-tooltip.top="'Mitu kilomeetrit lahusust on väärt üks protsendipunkt märgiga viga. 0 = puhtalt geograafilised klastrid; suurem väärtus lahutab sama piirkonna üle- ja alahinnatud tehingud eri klastritesse. Vaikimisi 0,5 (10pp viga ≈ 5 km).'"
                  />
                </label>
                <InputNumber
                  v-model="clusterResidualWeight"
                  :min="0"
                  :max="10"
                  :step="0.1"
                  :minFractionDigits="1"
                  class="w-36"
                />
              </div>
            </template>
            <Button
              :label="clusterMethod === 'kmeans' ? 'Klasterda' : 'Segmenteeri'"
              icon="pi pi-sitemap"
              :loading="clusterLoading"
              @click="runCluster"
            />
            <span v-if="clusterResult" class="text-sm text-surface-500">
              <template v-if="clusterResult.method === 'tree'">
                {{ clusterResult.k }} segmenti · sügavus {{ clusterResult.depth }}
                <span class="text-surface-400">(n = {{ clusterResult.params.n }}, min leht {{ clusterResult.params.min_leaf }})</span>
              </template>
              <template v-else-if="clusterResult.method === 'apriori'">
                {{ clusterResult.k }} segmenti · a-priori reeglid
                <span class="text-surface-400">
                  (n = {{ clusterResult.params.n }}, R = {{ clusterResult.params.radius_km }} km / {{ clusterResult.params.window_months }} kuud<template v-if="clusterResult.params.n_null_density > 0">, {{ clusterResult.params.n_null_density }} rida tiheduseta</template>)
                </span>
              </template>
              <template v-else>
                k = {{ clusterResult.k }} · siluett {{ clusterResult.silhouette }}
                <span v-if="clusterResult.params" class="text-surface-400">
                  (n = {{ clusterResult.params.n }}, kaal {{ clusterResult.params.residual_km_per_pct }})
                </span>
              </template>
            </span>
          </div>

          <Message v-if="clusterError" severity="error" :closable="false" class="mb-3">
            {{ clusterError }}
          </Message>

          <Message
            v-for="warning in clusterResult?.warnings ?? []"
            :key="warning"
            severity="warn"
            :closable="false"
            class="mb-3"
          >
            {{ warning }}
          </Message>

          <DataTable
            v-if="clusterResult"
            v-model:expandedRows="expandedClusters"
            :value="clusterResult.clusters"
            dataKey="id"
            size="small"
            stripedRows
            :rowHover="true"
            @row-click="e => zoomToCluster(e.data)"
          >
            <Column expander style="width: 3rem" />
            <Column header="" style="width: 3rem">
              <template #body="{ data }">
                <span
                  class="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold"
                  :style="{ backgroundColor: clusterColor(data.id) }"
                >
                  {{ data.id }}
                </span>
              </template>
            </Column>
            <Column header="Definitsioon">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <span class="text-sm">{{ data.dominant_asum ?? data.dominant_omavalitsus ?? '—' }}</span>
                  <Tag
                    v-if="data.definition?.rule"
                    :value="fidelityLabel(data.definition.rule.fidelity)"
                    :severity="fidelitySeverity(data.definition.rule.fidelity)"
                    class="text-[10px]!"
                  />
                </div>
                <div v-if="data.definition?.summary" class="text-xs text-surface-500">
                  {{ data.definition.summary }}
                </div>
                <div v-if="data.definition?.rule && data.definition.rule.fidelity !== 'poor'" class="text-xs font-medium text-surface-700 mt-0.5">
                  <i class="pi pi-filter text-[10px]" /> {{ data.definition.rule.text }}
                </div>
              </template>
            </Column>
            <Column field="n" header="n" style="width: 5rem" />
            <Column header="MAPE %" style="width: 7rem">
              <template #body="{ data }">{{ pct(data.mape) }}</template>
            </Column>
            <Column header="Bias %" style="width: 7rem">
              <template #body="{ data }">
                <span :class="biasClass(data.bias)">{{ pct(data.bias) }}</span>
              </template>
            </Column>
            <Column header="Raadius" style="width: 7rem">
              <template #body="{ data }">{{ data.radius_km }} km</template>
            </Column>

            <template #expansion="{ data }">
              <div v-if="data.definition?.rule" class="px-3 pt-3 bg-surface-50">
                <div class="rounded-md border p-3" :class="data.definition.rule.fidelity === 'poor' ? 'border-surface-300 bg-surface-100' : 'border-primary-200 bg-primary-50/40'">
                  <div class="flex flex-wrap items-center gap-2 mb-1">
                    <span class="font-medium text-sm">Reegel uue korteri jaoks</span>
                    <Tag
                      :value="fidelityLabel(data.definition.rule.fidelity)"
                      :severity="fidelitySeverity(data.definition.rule.fidelity)"
                    />
                    <span v-if="data.definition.rule.fidelity !== 'poor'" class="text-xs text-surface-500">
                      täpsus {{ Math.round(data.definition.rule.precision * 100) }}% ·
                      katvus {{ Math.round(data.definition.rule.recall * 100) }}% ·
                      F1 {{ data.definition.rule.f1 }}
                    </span>
                  </div>
                  <div class="text-sm">{{ data.definition.rule.text }}</div>
                  <div v-if="data.definition.rule.fidelity === 'poor'" class="text-xs text-surface-500 mt-1">
                    Klastri liikmeid eristab peamiselt vea märk/suurus, mida uuel korteril veel pole —
                    seda klastrit ei saa segmendina kasutada, aga ta näitab, KUS mudel eksib.
                  </div>
                </div>
              </div>
              <div v-if="data.definition" class="p-3 bg-surface-50 grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- Error band -->
                <div>
                  <div class="font-medium text-sm mb-2">Viga</div>
                  <Tag
                    :value="directionLabel(data.definition.error.direction)"
                    :severity="directionSeverity(data.definition.error.direction)"
                  />
                  <div class="text-sm mt-2">
                    mediaan
                    <span class="font-medium" :class="biasClass(data.definition.error.median)">
                      {{ pct(data.definition.error.median) }}
                    </span>
                  </div>
                  <div class="text-xs text-surface-500">
                    80% tehingutest vahemikus {{ pct(data.definition.error.p10) }} … {{ pct(data.definition.error.p90) }}
                  </div>
                </div>

                <!-- Region composition -->
                <div>
                  <div class="font-medium text-sm mb-2">Piirkondlik koosseis</div>
                  <div v-for="region in data.definition.regions" :key="region.name" class="mb-1">
                    <div class="flex justify-between text-xs">
                      <span>{{ region.name }}</span>
                      <span class="text-surface-500">{{ region.n }} · {{ Math.round(region.share * 100) }}%</span>
                    </div>
                    <div class="h-1.5 bg-surface-200 rounded-sm">
                      <div
                        class="h-1.5 rounded-sm"
                        :style="{ width: `${Math.max(region.share * 100, 2)}%`, backgroundColor: clusterColor(data.id) }"
                      />
                    </div>
                  </div>
                </div>

                <!-- Typical apartment -->
                <div>
                  <div class="font-medium text-sm mb-2">Tüüpiline korter (mediaan · kvartiilid)</div>
                  <div class="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 text-sm">
                    <template v-for="attr in CLUSTER_ATTR_LABELS" :key="attr.key">
                      <span class="text-surface-500 text-xs">{{ attr.label }}</span>
                      <span>
                        {{ attrNumber(data.definition.attributes[attr.key]?.median, attr.key) }}{{ attr.unit }}
                        <span class="text-surface-400 text-xs">
                          ({{ attrNumber(data.definition.attributes[attr.key]?.p25, attr.key) }}–{{ attrNumber(data.definition.attributes[attr.key]?.p75, attr.key) }})
                        </span>
                      </span>
                      <span
                        v-if="data.definition.attributes[attr.key]?.vs_overall_pct !== null && data.definition.attributes[attr.key]?.vs_overall_pct !== undefined"
                        class="text-xs"
                        :class="data.definition.attributes[attr.key].distinctive ? 'font-semibold text-amber-700' : 'text-surface-400'"
                        v-tooltip.left="'Erinevus kogu valimi mediaanist' + (data.definition.attributes[attr.key].distinctive ? ' — eristav tunnus' : '')"
                      >
                        {{ data.definition.attributes[attr.key].vs_overall_pct > 0 ? '+' : '' }}{{ data.definition.attributes[attr.key].vs_overall_pct }}%
                      </span>
                      <span v-else class="text-xs text-surface-300">—</span>
                    </template>
                  </div>
                </div>
              </div>
            </template>
          </DataTable>
          <p v-if="clusterResult && clusterResult.method === 'tree'" class="text-xs text-surface-400 mt-2">
            Segmendid on järjestatud halvima MAPE järgi. Iga segment on täpne reegel vaadeldavate
            tunnuste üle (piirkond, pind, vanus, tube, korrus, mudeli prognoos) — segmendid ei
            kattu ja katavad kogu ruumi, seega IGA tehing (ka uus korter) kuulub täpselt ühte:
            liigu puus tingimuste järgi. Puudleva/tundmatu tunnusega tehing läheb enamuse harusse
            (salvestatud puus, marsruutimine on alati üheselt korratav). Rea klõps suumib kaardile.
          </p>
          <p v-else-if="clusterResult" class="text-xs text-surface-400 mt-2">
            Klastrid on järjestatud halvima MAPE järgi — diagnostiline vaade: kus ja mis suunas
            mudel eksib, ka siis, kui seda ei saa vaadeldavate tunnustega eristada. Uue tehingu
            marsruutimiseks kasuta Segmendipuud; „Diagnostiline" klaster pole vaadeldavate
            tunnuste põhjal eristatav ega saa kunagi uut korterit.
          </p>
          <p v-else class="text-sm text-surface-400">
            Klasterdamist pole veel tehtud. Vali parameetrid ja vajuta „Klasterda".
          </p>
        </template>
      </Card>

      <!-- Per-segment config tuning -->
      <Card class="mb-4">
        <template #title>
          <div class="flex flex-wrap items-center gap-3">
            <span class="text-xl font-medium">Segmentide häälestus</span>
            <span class="text-sm text-surface-500">
              NSGA konfiguratsiooniotsing igale puu sõlmele + segmentatsioonide edetabel
            </span>
          </div>
        </template>
        <template #content>
          <!-- Launch / running / failed states -->
          <div v-if="!segmentTuning || segmentTuning.status === 'failed'" class="flex flex-wrap items-end gap-3 mb-2">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-surface-500 inline-flex items-center gap-1">
                Segmentatsioon
                <i
                  class="pi pi-info-circle text-surface-400 cursor-help"
                  v-tooltip.top="'CART: häälestab iga puu sõlme (hierarhiline edetabel). A-priori: häälestab salvestatud a-priori segmendid lameda jaotusena — globaalne vs segmendipõhine; vajab eelnevat A-priori segmenteerimist.'"
                />
              </label>
              <SelectButton
                v-model="tuneSegmentation"
                :options="tuneSegmentationOptions"
                optionLabel="label"
                optionValue="value"
                size="small"
              />
            </div>
            <div v-if="tuneSegmentation === 'cart'" class="flex flex-col gap-1">
              <label class="text-xs text-surface-500">Puu sügavus</label>
              <InputNumber v-model="tuneDepth" :min="1" :max="4" class="w-28" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-surface-500">Min tehinguid sõlmes</label>
              <InputNumber v-model="tuneMinLeaf" :min="20" :max="500" class="w-28" />
            </div>
            <Button label="Häälesta segmendid" icon="pi pi-cog" :loading="tuneLaunching" @click="launchTuning" />
            <span class="text-xs text-surface-400">
              Iga sõlme kohta üks NSGA otsing — kestab kümneid minuteid (calibration-järjekord).
            </span>
          </div>
          <Message v-if="segmentTuning?.status === 'failed'" severity="error" :closable="false" class="mb-2">
            Häälestus ebaõnnestus: {{ segmentTuning.error }}
          </Message>
          <div v-else-if="segmentTuning?.status === 'running'" class="flex items-center gap-2 text-sm">
            <i class="pi pi-spin pi-spinner text-primary" />
            <span>{{ segmentTuning.progress_message ?? 'Häälestan…' }}</span>
            <span v-if="segmentTuning.nodes_total" class="text-surface-400">
              ({{ segmentTuning.nodes_done ?? 0 }}/{{ segmentTuning.nodes_total }} sõlme)
            </span>
            <Button label="Värskenda" size="small" text icon="pi pi-refresh" @click="loadDetail({ id: detail.run.id })" />
          </div>

          <template v-else-if="segmentTuning?.status === 'completed'">
            <!-- Segmentation ranking -->
            <div class="font-medium text-sm mb-1">Segmentatsioonide edetabel (mudel {{ segmentTuning.model }})</div>
            <DataTable :value="segmentTuning.cuts" size="small" stripedRows class="mb-4">
              <Column header="">
                <template #body="{ index }">
                  <Tag v-if="index === 0" value="parim" severity="success" />
                </template>
              </Column>
              <Column field="name" header="Lõige" />
              <Column field="k" header="Segmente" style="width: 7rem" />
              <Column header="Häälestatud MAPE %" style="width: 11rem">
                <template #body="{ data }">
                  <span class="font-medium">{{ pct(data.weighted_tuned_mape) }}</span>
                </template>
              </Column>
              <Column header="Baseline MAPE %" style="width: 10rem">
                <template #body="{ data }">{{ pct(data.weighted_baseline_mape) }}</template>
              </Column>
              <Column header="Paranemine" style="width: 8rem">
                <template #body="{ data }">
                  <span v-if="improvementPct(data) !== null" :class="improvementPct(data) > 0 ? 'text-green-600 font-medium' : 'text-surface-400'">
                    {{ improvementPct(data) > 0 ? '−' : '+' }}{{ Math.abs(improvementPct(data)) }}%
                  </span>
                  <span v-else>—</span>
                </template>
              </Column>
            </DataTable>

            <!-- Every tuned node with its verdict + config (the best cut is tagged) -->
            <div class="font-medium text-sm mb-1">Sõlmede tulemused ja konfiguratsioonid</div>
            <DataTable :value="tunedNodes" size="small" stripedRows>
              <Column header="Segment">
                <template #body="{ data }">
                  <div class="text-sm">
                    {{ nodeLabel(data) }}
                    <Tag v-if="bestCutKeys.has(data.key)" value="parimas lõikes" severity="info" class="ml-1" />
                  </div>
                  <div class="text-xs text-surface-400">{{ data.key }}</div>
                </template>
              </Column>
              <Column field="n" header="n" style="width: 5rem" />
              <Column header="MAPE (baseline → häälestatud)" style="width: 14rem">
                <template #body="{ data }">
                  <template v-if="data.skipped">
                    <span class="text-surface-400">skaneeringu MAPE {{ pct(data.scan_mape) }}</span>
                  </template>
                  <template v-else>
                    <span class="text-surface-400">{{ pct(data.baseline_mape) }}</span>
                    <i class="pi pi-arrow-right mx-1 text-xs" />
                    <span class="font-medium" :class="{ 'text-green-600': data.beats_baseline }">{{ pct(data.tuned_mape) }}</span>
                    <div v-if="data.tuned_median !== null && data.tuned_median !== undefined" class="text-xs text-surface-400">
                      mediaan {{ pct(data.baseline_median) }} → {{ pct(data.tuned_median) }}
                    </div>
                  </template>
                </template>
              </Column>
              <Column header="Kas segmenti tasub luua?" style="width: 12rem">
                <template #body="{ data }">
                  <Tag v-if="data.key === 'root'" value="globaalne kandidaat" severity="secondary" />
                  <Tag
                    v-else-if="data.skipped"
                    value="Vahele jäetud — liiga väike"
                    severity="warn"
                  />
                  <Tag
                    v-else
                    :value="data.beats_baseline ? 'Jah — võidab globaalset' : 'Ei — globaalne piisab'"
                    :severity="data.beats_baseline ? 'success' : 'secondary'"
                  />
                </template>
              </Column>
              <Column header="Häälestatud kaalud">
                <template #body="{ data }">
                  <span class="text-xs">{{ configText(data.config) }}</span>
                </template>
              </Column>
            </DataTable>
            <p class="text-xs text-surface-400 mt-2">
              Edetabel: iga lõike skoor = liikmete arvuga kaalutud MAPE, kui iga segment kasutaks
              OMA häälestatud konfiguratsiooni. „Optimaalne" lõige aktsepteerib jagamise ainult
              siis, kui lapsed võidavad vanemat ≥ {{ 3 }}% võrra (ülesobitamise kaitse).
              Rakendamine (SegmentResolver + dünaamiline SimilarityConfig) on eraldi samm — see
              vaade annab tõendid ja täpsed konfiguratsioonid.
            </p>

            <div v-if="canPromoteSegments" class="mt-3 flex items-center gap-3">
              <Button
                label="Rakenda segmendid"
                icon="pi pi-check-circle"
                severity="success"
                data-testid="promote-segments"
                @click="promoteSegments"
              />
              <span class="text-xs text-surface-400">
                Külmutab võitvate segmentide konfiguratsioonid aktiivseks segmentatsiooniks
                (ülejäänud pärivad globaalse). Serveerimine eeldab „Segmendipõhised valikukaalud"
                funktsioonilippu.
              </span>
            </div>
          </template>
        </template>
      </Card>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <!-- Regions -->
        <Card>
          <template #title>
            <div class="flex items-center justify-between gap-2">
              <span class="text-xl font-medium">Piirkonnad</span>
              <SelectButton
                v-model="regionLevel"
                :options="regionLevelOptions"
                optionLabel="label"
                optionValue="value"
                size="small"
              />
            </div>
          </template>
          <template #content>
            <DataTable
              :value="regionRows"
              size="small"
              stripedRows
              sortField="mape"
              :sortOrder="-1"
              paginator
              :rows="15"
              :rowClass="row => (row.low_n ? 'opacity-45' : '')"
            >
              <Column field="label" header="Piirkond" sortable />
              <Column field="n" header="n" sortable style="width: 5rem" />
              <Column field="mape" header="MAPE %" sortable style="width: 7rem">
                <template #body="{ data }">{{ pct(data.mape) }}</template>
              </Column>
              <Column field="bias" header="Bias %" sortable style="width: 7rem">
                <template #body="{ data }">
                  <span :class="biasClass(data.bias)">{{ pct(data.bias) }}</span>
                </template>
              </Column>
              <template #empty>
                <div class="text-center text-surface-400 py-4">Piirkondlikke andmeid pole.</div>
              </template>
            </DataTable>
            <p class="text-xs text-surface-400 mt-2">
              Hallid read: alla {{ 3 }} tehingu — MAPE on müra, mitte signaal.
            </p>
          </template>
        </Card>

        <!-- Attributes -->
        <Card>
          <template #title>
            <span class="text-xl font-medium">Atribuudisegmendid</span>
          </template>
          <template #content>
            <DataTable
              :value="attributeRows"
              size="small"
              stripedRows
              rowGroupMode="subheader"
              groupRowsBy="group"
              :rowClass="row => (row.low_n ? 'opacity-45' : '')"
            >
              <Column field="group" header="Grupp" />
              <Column field="label" header="Segment" />
              <Column field="n" header="n" style="width: 5rem" />
              <Column field="mape" header="MAPE %" style="width: 7rem">
                <template #body="{ data }">{{ pct(data.mape) }}</template>
              </Column>
              <Column field="bias" header="Bias %" style="width: 7rem">
                <template #body="{ data }">
                  <span :class="biasClass(data.bias)">{{ pct(data.bias) }}</span>
                </template>
              </Column>
              <template #groupheader="{ data }">
                <span class="font-medium text-sm">{{ data.group }}</span>
              </template>
              <template #empty>
                <div class="text-center text-surface-400 py-4">Segmendiandmeid pole.</div>
              </template>
            </DataTable>
          </template>
        </Card>
      </div>
    </template>

    <PromotionApprovalDialog
      v-model:visible="promoteDialogVisible"
      :url="promoteSegmentsUrl"
      title="Rakenda segmendid"
      :subject="detail?.run ? `Segmendianalüüs #${detail.run.id}` : ''"
    />
  </div>
</template>
